import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { ArrowLeft, Save, Upload, Link as LinkIcon, Type, FileText, PlayCircle, Image as ImageIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { api, API_BASE_URL } from '../../api';

interface Module {
  _id: string;
  title: string;
  description?: string;
  type: 'video' | 'pdf' | 'text' | 'link' | 'image' | 'document';
  content?: string;
  fileUrl?: string;
  externalUrl?: string;
  duration?: number;
  order?: number;
  isRequired?: boolean;
}

const AdminCourseModuleEdit: React.FC = () => {
  const { courseId, sectionId, moduleId } = useParams();
  const navigate = useNavigate();
  const [module, setModule] = useState<Module | null>(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState<Partial<Module>>({
    type: 'text',
    isRequired: false,
    duration: 0,
  });
  const [file, setFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);

  useEffect(() => {
    const fetchModule = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/courses/${courseId}/modules/${moduleId}`);
        if (response.data && response.data.data) {
          const fetchedModule = response.data.data;
          setModule(fetchedModule);
          setFormData({
            title: fetchedModule.title,
            description: fetchedModule.description,
            type: fetchedModule.type,
            content: fetchedModule.content,
            fileUrl: fetchedModule.fileUrl,
            externalUrl: fetchedModule.externalUrl,
            duration: fetchedModule.duration,
            order: fetchedModule.order,
            isRequired: fetchedModule.isRequired,
          });
          if (fetchedModule.fileUrl) {
            setFilePreview(`${API_BASE_URL}${fetchedModule.fileUrl}`);
          }
        } else {
          toast.error('Module non trouvé');
          navigate(`/admin/courses/${courseId}`);
        }
      } catch (error) {
        console.error('Error fetching module:', error);
        toast.error('Erreur lors du chargement du module');
        navigate(`/admin/courses/${courseId}`);
      } finally {
        setLoading(false);
      }
    };

    fetchModule();
  }, [moduleId, courseId, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);
      
      // Create preview for images
      if (selectedFile.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = () => {
          setFilePreview(reader.result as string);
        };
        reader.readAsDataURL(selectedFile);
      } else {
        setFilePreview(null);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const submitData = new FormData();
    for (const key in formData) {
      if (formData[key as keyof typeof formData] !== undefined) {
        submitData.append(key, String(formData[key as keyof typeof formData]));
      }
    }
    if (file) {
      submitData.append('file', file);
    }

    try {
      const response = await api.put(`/courses/${courseId}/sections/${sectionId}/modules/${moduleId}`, submitData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data) {
        toast.success('Module mis à jour avec succès');
        navigate(`/admin/courses/${courseId}/section/${sectionId}/module/${moduleId}`);
      } else {
        toast.error('Erreur lors de la mise à jour du module');
      }
    } catch (error) {
      console.error('Error updating module:', error);
      toast.error('Erreur lors de la mise à jour du module');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
        </div>
      </Layout>
    );
  }

  if (!module) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Module non trouvé</h3>
          <button
            onClick={() => navigate(`/admin/courses/${courseId}`)}
            className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
          >
            Retour au cours
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <button
              onClick={() => navigate(`/admin/courses/${courseId}/section/${sectionId}/module/${moduleId}`)}
              className="mr-4 text-gray-500 hover:text-gray-700"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-3xl font-bold text-gray-900">Modifier le module: {module.title}</h1>
          </div>
          <button
            onClick={handleSubmit}
            className="flex items-center px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
          >
            <Save className="h-4 w-4 mr-2" />
            Enregistrer
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700">Titre du module *</label>
              <input
                type="text"
                name="title"
                id="title"
                value={formData.title || ''}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                required
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">Description</label>
              <textarea
                name="description"
                id="description"
                value={formData.description || ''}
                onChange={handleChange}
                rows={3}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand-500 focus:border-brand-500"
              />
            </div>

            <div>
              <label htmlFor="type" className="block text-sm font-medium text-gray-700">Type de contenu *</label>
              <select
                name="type"
                id="type"
                value={formData.type || ''}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                required
              >
                <option value="video">Vidéo</option>
                <option value="pdf">PDF</option>
                <option value="text">Texte</option>
                <option value="link">Lien externe</option>
                <option value="image">Image</option>
                <option value="document">Document</option>
              </select>
            </div>

            {/* Content based on type */}
            {formData.type === 'text' && (
              <div>
                <label htmlFor="content" className="block text-sm font-medium text-gray-700">Contenu textuel *</label>
                <textarea
                  name="content"
                  id="content"
                  value={formData.content || ''}
                  onChange={handleChange}
                  rows={10}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                  required
                />
              </div>
            )}

            {(formData.type === 'video' || formData.type === 'pdf' || formData.type === 'image' || formData.type === 'document') && (
              <div>
                <label htmlFor="file" className="block text-sm font-medium text-gray-700">
                  {formData.type === 'video' ? 'Fichier vidéo' : 
                   formData.type === 'pdf' ? 'Fichier PDF' : 
                   formData.type === 'image' ? 'Image' : 'Document'}
                </label>
                <input
                  type="file"
                  name="file"
                  id="file"
                  onChange={handleFileChange}
                  accept={
                    formData.type === 'video' ? 'video/*' : 
                    formData.type === 'pdf' ? 'application/pdf' : 
                    formData.type === 'image' ? 'image/*' : 
                    '.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf'
                  }
                  className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100"
                />
                {(filePreview || formData.fileUrl) && (
                  <div className="mt-4">
                    {formData.type === 'image' ? (
                      <img 
                        src={filePreview || `${API_BASE_URL}${formData.fileUrl}`} 
                        alt="Preview" 
                        className="h-40 object-contain border rounded"
                      />
                    ) : (
                      <p className="text-sm text-gray-600">
                        Fichier actuel: {formData.fileUrl ? (
                          <a 
                            href={`${API_BASE_URL}${formData.fileUrl}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-brand-600 hover:underline"
                          >
                            {formData.fileUrl.split('/').pop()}
                          </a>
                        ) : 'Aucun fichier sélectionné'}
                      </p>
                    )}
                  </div>
                )}
                <div className="mt-2">
                  <label htmlFor="content" className="block text-sm font-medium text-gray-700">
                    Ou URL {formData.type === 'video' ? 'de vidéo (YouTube, Vimeo, etc.)' : 'directe'}
                  </label>
                  <input
                    type="url"
                    name="content"
                    id="content"
                    value={formData.content || ''}
                    onChange={handleChange}
                    placeholder={
                      formData.type === 'video' ? 'https://www.youtube.com/watch?v=...' : 
                      formData.type === 'pdf' ? 'https://example.com/document.pdf' : 
                      formData.type === 'image' ? 'https://example.com/image.jpg' : 
                      'https://example.com/document'
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>
              </div>
            )}

            {formData.type === 'link' && (
              <div>
                <label htmlFor="externalUrl" className="block text-sm font-medium text-gray-700">URL du lien *</label>
                <input
                  type="url"
                  name="externalUrl"
                  id="externalUrl"
                  value={formData.externalUrl || ''}
                  onChange={handleChange}
                  placeholder="https://example.com"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                  required
                />
              </div>
            )}

            <div>
              <label htmlFor="duration" className="block text-sm font-medium text-gray-700">Durée estimée (minutes) *</label>
              <input
                type="number"
                name="duration"
                id="duration"
                min="0"
                value={formData.duration || 0}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                required
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="isRequired"
                id="isRequired"
                checked={formData.isRequired || false}
                onChange={handleChange}
                className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-gray-300 rounded"
              />
              <label htmlFor="isRequired" className="ml-2 block text-sm text-gray-900">
                Module requis (les étudiants doivent le compléter)
              </label>
            </div>

            <div className="flex justify-end space-x-3 pt-6">
              <button
                type="button"
                onClick={() => navigate(`/admin/courses/${courseId}/section/${sectionId}/module/${moduleId}`)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
              >
                <Save className="h-4 w-4 mr-2" />
                Enregistrer les modifications
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default AdminCourseModuleEdit;